import _find from 'lodash/find';
import { useMemo } from 'react';
import { Table } from 'ui/table';
import { TableHeader } from 'ui/tableHeader';
import { TableRows } from 'ui/tableRows';
import { FlexSpacer } from 'ui/flexSpacer';
import { checkIfParentGeo } from '../helpers';
import { MODE } from '../constants';
import {
  FIELD_TYPE_TABLE_COLUMNS_VIEW_MODE_1_SAMPLE,
  FIELD_TYPE_TABLE_COLUMNS_VIEW_MODE_2_SAMPLES,
  configureFieldTypeTableColumnsEdit,
  FIELD_TYPE_TABLE_COLUMNS_CONFIRM_MODE_2_SAMPLES,
  FIELD_TYPE_TABLE_COLUMNS_CONFIRM_MODE_1_SAMPLE,
} from './DefineFieldTypesTableColumns';
import { DefineFieldTypeCSSContainer } from './FieldTypes.styled';
import type { FieldTypeTableRow } from '../types';

type DefineFieldTypesProps = {
  dropdownOnChangeHandler: (value: any, fieldName: string) => void;
  fields: Record<string, string | undefined>;
  rows: FieldTypeTableRow[];
  showSingleSample?: boolean;
  undefinedFields: string[];
  viewMode: keyof typeof MODE;
};

export const DefineFieldTypes = ({
  dropdownOnChangeHandler,
  fields,
  rows,
  showSingleSample = false,
  undefinedFields,
  viewMode,
}: DefineFieldTypesProps) => {
  const renderedRows = useMemo(() => {
    switch (viewMode) {
      case MODE.view:
        return rows;
      case MODE.define:
        return undefinedFields
          .filter((row) => {
            // check if row name is an object in dot notation
            // if parent type is geo_location, then hide the related child rows
            const isGeo = checkIfParentGeo({ keysInDotNotation: row, fields });
            // remove from fields object
            if (isGeo) {
              delete fields[row];
            }
            return !isGeo;
          })
          .map((row, i) => {
            const data = _find(rows, { fieldName: row });
            return {
              ...data,
              fieldName: row,
              fieldType: fields[row],
              id: i,
            };
          });
      case MODE.confirm:
        return undefinedFields
          .filter((row) => !checkIfParentGeo({ keysInDotNotation: row, fields }))
          .map((row, i) => ({
            ..._find(rows, { fieldName: row }),
            id: i,
            fieldName: row,
            fieldType: fields[row],
          }));
      default:
        return rows;
    }
  }, [rows, viewMode, undefinedFields, fields]);

  const renderedColumns = useMemo(() => {
    switch (viewMode) {
      case MODE.view:
        return showSingleSample
          ? FIELD_TYPE_TABLE_COLUMNS_VIEW_MODE_1_SAMPLE
          : FIELD_TYPE_TABLE_COLUMNS_VIEW_MODE_2_SAMPLES;
      case MODE.define:
        return configureFieldTypeTableColumnsEdit(showSingleSample, (value, row) => {
          dropdownOnChangeHandler(value, row);
        });
      case MODE.confirm:
      default:
        return showSingleSample
          ? FIELD_TYPE_TABLE_COLUMNS_CONFIRM_MODE_1_SAMPLE
          : FIELD_TYPE_TABLE_COLUMNS_CONFIRM_MODE_2_SAMPLES;
    }
  }, [viewMode, showSingleSample, dropdownOnChangeHandler]);

  return (
    <DefineFieldTypeCSSContainer isViewMode={viewMode === MODE.view}>
      <Table>
        <TableHeader columns={renderedColumns} />
        <TableRows rows={renderedRows} columns={renderedColumns} />
      </Table>
      {renderedRows.length === 0 && (
        <FlexSpacer padding="cardPadding" justifyContent="center">
          No fields found
        </FlexSpacer>
      )}
    </DefineFieldTypeCSSContainer>
  );
};
